// frontend/src/lib/notifications-api.ts
/**
 * Notifications API client — list, unread count, mark read.
 */

import { apiClient } from '@/lib/api-client'

export type NotificationType = 'info' | 'success' | 'warning' | 'error'
export type NotificationCategory = 'policy' | 'probing' | 'severity'

export interface Notification {
  id: string
  type: NotificationType
  category: NotificationCategory
  title: string
  message: string
  resource_type: string | null
  resource_id: string | null
  resource_key: string | null
  is_read: boolean
  read_at: string | null
  created_at: string
}

export interface NotificationListParams {
  unread_only?: boolean
  limit?: number
  offset?: number
}

export interface NotificationListResponse {
  items: Notification[]
  total: number
  unread_count: number
  limit: number
  offset: number
}

export interface UnreadCountResponse {
  unread_count: number
}

function buildQuery(params: NotificationListParams): string {
  const search = new URLSearchParams()
  if (params.unread_only) search.set('unread_only', 'true')
  if (params.limit != null) search.set('limit', String(params.limit))
  if (params.offset != null) search.set('offset', String(params.offset))
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

export const notificationsApi = {
  list: (params: NotificationListParams = {}): Promise<NotificationListResponse> =>
    apiClient.get<NotificationListResponse>(`/api/notifications${buildQuery(params)}`),

  unreadCount: (): Promise<UnreadCountResponse> =>
    apiClient.get<UnreadCountResponse>('/api/notifications/unread-count'),

  markRead: (notificationId: string): Promise<Notification> =>
    apiClient.patch<Notification>(`/api/notifications/${notificationId}/read`),

  markAllRead: (): Promise<{ updated: number }> =>
    apiClient.post<{ updated: number }>('/api/notifications/mark-all-read'),
}
